import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ShieldCheck, Copy, Check, AlertTriangle } from 'lucide-react';
import { beginMfaEnrollment, confirmMfaEnrollment, mfaErrorMessage } from '@/lib/adminMfa';

/**
 * One-time TOTP setup for the admin console. Shown when the admin is signed in
 * but has no authenticator enrolled yet.
 */
const AdminMfaEnrollment = ({ onComplete }) => {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [savedAcknowledged, setSavedAcknowledged] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await beginMfaEnrollment();
        if (!cancelled) setSetup(data);
      } catch (err) {
        if (!cancelled) setError(mfaErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCopySecret = async () => {
    if (!setup?.secret) return;
    try {
      await navigator.clipboard.writeText(setup.secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError('Could not copy — select the key and copy it manually.');
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (code.replace(/\s/g, '').length < 6) {
      setError('Enter the 6-digit code from your authenticator.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await confirmMfaEnrollment(code);
      setRecoveryCodes(result?.recoveryCodes || []);
    } catch (err) {
      setError(mfaErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDownloadCodes = () => {
    const body = [
      'Your Song Request — admin recovery codes',
      'Each code works once. Store them somewhere safe and offline.',
      '',
      ...(recoveryCodes || []),
      '',
    ].join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'ysr-admin-recovery-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  if (recoveryCodes) {
    return (
      <Shell title="Save your recovery codes" description="These are shown once and cannot be retrieved again.">
        <div className="rounded-lg border border-yellow-700/40 bg-yellow-900/20 p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400" />
            <p className="text-sm text-yellow-300">
              Each code works once, and lets you back in if you lose access to your
              authenticator. Save them in iCloud Passwords or print them now.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 rounded-lg border border-gray-700 bg-gray-950/60 p-4 font-mono text-sm text-gray-200">
          {recoveryCodes.map((rc) => <span key={rc}>{rc}</span>)}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row">
          <Button type="button" variant="outline" className="flex-1 border-gray-600 text-gray-200" onClick={handleDownloadCodes}>
            Download as a text file
          </Button>
          <Button
            type="button"
            variant="outline"
            className="flex-1 border-gray-600 text-gray-200"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(recoveryCodes.join('\n'));
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch { /* clipboard unavailable */ }
            }}
          >
            {copied ? 'Copied' : 'Copy all'}
          </Button>
        </div>

        <label className="flex items-start gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            className="mt-1"
            checked={savedAcknowledged}
            onChange={(e) => setSavedAcknowledged(e.target.checked)}
          />
          I have saved these codes somewhere safe.
        </label>

        <Button
          type="button"
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 font-semibold"
          disabled={!savedAcknowledged}
          onClick={onComplete}
        >
          Continue to the admin console
        </Button>
      </Shell>
    );
  }

  return (
    <Shell
      title="Set up two-factor authentication"
      description="The admin console needs a 6-digit code as well as your password."
    >
      {loading && <p className="text-sm text-gray-400">Preparing your setup key…</p>}

      {!loading && setup && (
        <>
          <ol className="space-y-2 text-sm text-gray-300">
            <li><span className="font-semibold text-white">1.</span> Scan this QR code with iCloud Passwords, or your authenticator app.</li>
            <li><span className="font-semibold text-white">2.</span> Enter the 6-digit code it shows to confirm.</li>
          </ol>

          <div className="flex justify-center rounded-lg bg-white p-4">
            <QRCodeSVG value={setup.otpauthUri} size={190} level="M" />
          </div>

          <div className="space-y-2">
            <p className="text-xs text-gray-400">
              Can&apos;t scan? Add it by hand with this key:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all rounded border border-gray-700 bg-gray-950/60 px-3 py-2 font-mono text-xs text-gray-200">
                {setup.secret}
              </code>
              <Button type="button" variant="outline" size="sm" className="border-gray-600" onClick={handleCopySecret}>
                {copied ? <Check className="h-4 w-4 text-green-400" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <form onSubmit={handleConfirm} className="space-y-4">
            <Input
              value={code}
              onChange={(e) => { setCode(e.target.value); setError(''); }}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={7}
              className="bg-gray-800/50 text-center text-2xl tracking-[0.4em] text-white"
              disabled={busy}
            />
            {error && <p className="text-sm text-red-400">{error}</p>}
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-red-500 to-orange-500 font-semibold"
              disabled={busy}
            >
              {busy ? 'Verifying…' : 'Confirm and enable'}
            </Button>
          </form>
        </>
      )}

      {!loading && !setup && error && (
        <div className="rounded-lg border border-red-700/40 bg-red-900/20 p-4 text-sm text-red-300">{error}</div>
      )}
    </Shell>
  );
};

const Shell = ({ title, description, children }) => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 p-4">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-md"
    >
      <Card className="border-gray-700 bg-gray-900/80 text-white backdrop-blur-sm">
        <CardHeader className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-orange-500">
            <ShieldCheck className="h-7 w-7 text-white" />
          </div>
          <div>
            <CardTitle className="text-xl font-bold text-white">{title}</CardTitle>
            <CardDescription className="text-gray-400">{description}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">{children}</CardContent>
      </Card>
    </motion.div>
  </div>
);

export default AdminMfaEnrollment;
